import * as R from 'ramda';
import {
  ServiceBrand,
  resolveServiceBrandFromHostname,
} from '@practicegenius/domain/src/data/serviceBrand';
import {IncomingHttpHeaders} from 'http';

export const CSRF_HEADER_NAME = 'x-csrf-token';
export const RECAPTCHA_TOKEN_HEADER_NAME = 'x-recaptcha-token';
export const RECAPTCHA_PLATFORM_HEADER_NAME = 'x-recaptcha-platform';
/** Used for s2s authentication with session id */
export const SESSION_ID_HEADER_NAME = 'x-session-id';
/** Used in modern s2s authentication to ensure requests come from known server */
export const KNOWN_HOST_HEADER_NAME = 'x-known-host';

/** Get and validate the host header from the request */
export const parseHost = (headers: IncomingHttpHeaders) => {
  // TODO If we ever add hubbuxteams.com or any other brands/flavors, then we'll need to update this. Maybe this should live in a config file somewhere?
  const allowedDomains = ['patientrewardshub', 'hubbux', 'culturello'];
  const hostHeaderNames = ['x-forwarded-host', 'host'];

  /** Given a list of keys, get the first non-nil value from the headers object */
  const getFirstHeaderValue = (keys: string[]) => {
    for (const key of keys) {
      const result = headers[key] as string;
      if (result) return result;
    }
  };

  /** Check that the proposed host against an allow-list of expected domain names */
  const validateHost = (proposedHost: string | undefined) => {
    if (!proposedHost) return;

    const [, domain] = proposedHost?.split('.');
    if (allowedDomains.includes(domain as string)) return proposedHost;
  };

  return validateHost(getFirstHeaderValue(hostHeaderNames));
};

export const parseServiceBrand = (
  headers: IncomingHttpHeaders,
): ServiceBrand => {
  const host = parseHost(headers);

  if (!host) return 'patientrewardshub';

  return resolveServiceBrandFromHostname(host);
};

export const parseReferrer = (headers: IncomingHttpHeaders) => {
  const referrerHeaderNames = ['referer', 'referrer'];

  for (const key of referrerHeaderNames) {
    const result = headers[key] as string;
    if (result) return result;
  }
};

/** Parsing this header isn't trivial because it can be an array, an empty string, or a comma-separated list of values
 * @see https://github.com/deis/deis/issues/4997
 */
const parseProtocolFromXForwardedProtoHeader = (
  header: string | string[] | undefined,
): string | undefined => {
  if (!header || R.isEmpty(header)) return undefined;

  const result = Array.isArray(header) ? header[0] : header;

  if (result === 'http' || result === 'https') {
    return result;
  }

  if (result.includes(',')) {
    const parts = result.split(',');
    if (parts.includes('https')) return 'https';
    if (parts.includes('http')) return 'http';
  }

  return undefined;
};

/** Builds a full URL with the hostname and HTTP protocol parsed from headers */
export const getBaseUrl = (headers: IncomingHttpHeaders) => {
  const host = parseHost(headers);
  if (!host) return null;

  const protocol =
    parseProtocolFromXForwardedProtoHeader(headers['x-forwarded-proto']) ??
    'https';
  return `${protocol}://${host}`;
};

export const getUserAgent = (headers: IncomingHttpHeaders) =>
  headers['user-agent'];
